import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import PricingPage from "./pages/PricingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SubscriptionGate from "./components/SubscriptionGate";
import LandlordDashboard from "./pages/landlord/Dashboard";
import LandlordProperties from "./pages/landlord/Properties";
import LandlordUnits from "./pages/landlord/Units";
import LandlordTenants from "./pages/landlord/Tenants";
import LandlordLeases from "./pages/landlord/Leases";
import LandlordPayments from "./pages/landlord/Payments";
import LandlordMaintenance from "./pages/landlord/Maintenance";
import LandlordSubscription from "./pages/landlord/Subscription";
import LandlordSettings from "./pages/landlord/Settings";
import LandlordReports from "./pages/landlord/Reports";
import TenantDashboard from "./pages/tenant/Dashboard";
import TenantLease from "./pages/tenant/Lease";
import TenantPayments from "./pages/tenant/Payments";
import TenantMaintenance from "./pages/tenant/Maintenance";
import TenantNotifications from "./pages/tenant/Notifications";
import TenantDocuments from "./pages/tenant/Documents";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminLandlords from "./pages/admin/Landlords";
import AdminOrganisations from "./pages/admin/Organisations";
import AdminPayments from "./pages/admin/Payments";
import BillingHistory from "./pages/landlord/BillingHistory";
import AuditLogs from "./pages/shared/AuditLogs";
import { I18nProvider } from "./context/I18nContext";
import ChatBot from "./components/ChatBot";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-gold">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    if (user?.role === 'SUPER_ADMIN') {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === 'LANDLORD') {
      return <Navigate to="/landlord" replace />;
    } else {
      return <Navigate to="/tenant" replace />;
    }
  }

  return children;
};

// Redirect based on role — authenticated users go to dashboard, unauthenticated go to landing
const RoleBasedRedirect = () => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-gold">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage />;
  }

  if (user?.role === 'SUPER_ADMIN') {
    return <Navigate to="/admin" replace />;
  } else if (user?.role === 'LANDLORD') {
    return <Navigate to="/landlord" replace />;
  } else {
    return <Navigate to="/tenant" replace />;
  }
};

function App() {
  return (
    <I18nProvider>
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background">
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />

            {/* Role-based redirect */}
            <Route path="/" element={<RoleBasedRedirect />} />

            {/* Landlord Routes */}
            <Route
              path="/landlord"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <SubscriptionGate><LandlordDashboard /></SubscriptionGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/properties"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <SubscriptionGate><LandlordProperties /></SubscriptionGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/units"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <SubscriptionGate><LandlordUnits /></SubscriptionGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/tenants"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <SubscriptionGate><LandlordTenants /></SubscriptionGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/leases"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <SubscriptionGate><LandlordLeases /></SubscriptionGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/payments"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <SubscriptionGate><LandlordPayments /></SubscriptionGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/maintenance"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <SubscriptionGate><LandlordMaintenance /></SubscriptionGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/subscription"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <LandlordSubscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/subscription/success"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <LandlordSubscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/settings"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <LandlordSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/reports"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <LandlordReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/billing"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <BillingHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/landlord/audit-logs"
              element={
                <ProtectedRoute allowedRoles={['LANDLORD', 'SUPER_ADMIN']}>
                  <AuditLogs type="landlord" />
                </ProtectedRoute>
              }
            />

            {/* Tenant Routes */}
            <Route
              path="/tenant"
              element={
                <ProtectedRoute allowedRoles={['TENANT']}>
                  <TenantDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenant/lease"
              element={
                <ProtectedRoute allowedRoles={['TENANT']}>
                  <TenantLease />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenant/payments"
              element={
                <ProtectedRoute allowedRoles={['TENANT']}>
                  <TenantPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenant/payments/success"
              element={
                <ProtectedRoute allowedRoles={['TENANT']}>
                  <TenantPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenant/maintenance"
              element={
                <ProtectedRoute allowedRoles={['TENANT']}>
                  <TenantMaintenance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenant/notifications"
              element={
                <ProtectedRoute allowedRoles={['TENANT']}>
                  <TenantNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenant/documents"
              element={
                <ProtectedRoute allowedRoles={['TENANT']}>
                  <TenantDocuments />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/landlords"
              element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <AdminLandlords />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/organisations"
              element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <AdminOrganisations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payments"
              element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <AdminPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <AuditLogs type="admin" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" theme="dark" />
          <ChatBot />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </I18nProvider>
  );
}

export default App;
